/**
 * Adds a password strength meter to a password input and exposes
 * zxcbn's verdict as sibling input.
 *
 * @author	Tim Duesterhus
 * @copyright	2001-2020 WoltLab GmbH
 * @license	GNU Lesser General Public License <http://opensource.org/licenses/lgpl-license.php>
 * @woltlabExcludeBundle tiny
 */
define('WoltLabSuite/Core/Ui/User/PasswordStrength',["require", "exports", "tslib", "WoltLabSuite/Core/Language", "../../Dom/Util"], function (require, exports, tslib_1, Language_1, Util_1) {
    "use strict";
    Util_1 = tslib_1.__importDefault(Util_1);
    const STATIC_DICTIONARY = [];
    const siteName = document.querySelector('meta[property="og:site_name"]')?.getAttribute("content");
    if (siteName) {
        STATIC_DICTIONARY.push(siteName);
    }
    function flatMap(array, callback) {
        return array.map(callback).reduce((carry, item) => {
            return carry.concat(item);
        }, []);
    }
    function splitIntoWords(value) {
        return [].concat(value, value.split(/\W+/));
    }
    function initializeFeedbacker(Feedback) {
        const localizedPhrases = {};
        Object.entries(Feedback.default_phrases).forEach(([type, phrases]) => {
            localizedPhrases[type] = {};
            Object.entries(phrases).forEach(([identifier, phrase]) => {
                const languageItem = `wcf.user.password.zxcvbn.${type}.${identifier}`;
                const localizedValue = (0, Language_1.getPhrase)(languageItem);
                localizedPhrases[type][identifier] = localizedValue !== languageItem ? localizedValue : phrase;
            });
        });
        return new Feedback(localizedPhrases);
    }
    class PasswordStrength {
        input;
        zxcvbn;
        relatedInputs = [];
        staticDictionary = [];
        feedbacker;
        wrapper;
        score = document.createElement("span");
        verdictResult = document.createElement("input");
        constructor(input, options) {
            this.input = input;
            void new Promise((resolve_1, reject_1) => { require(["zxcvbn"], resolve_1, reject_1); }).then(tslib_1.__importStar).then(({ default: zxcvbn }) => {
                this.zxcvbn = zxcvbn;
                if (options.relatedInputs) {
                    this.relatedInputs = options.relatedInputs;
                }
                if (options.staticDictionary) {
                    this.staticDictionary = options.staticDictionary;
                }
                this.feedbacker = initializeFeedbacker(zxcvbn.Feedback);
                const wrapper = this.input.closest(".inputAddon");
                if (wrapper === null) {
                    throw new Error("Expected a parent with `.inputAddon`.");
                }
                this.wrapper = wrapper;
                this.wrapper.classList.add("inputAddonPasswordStrength");
                const rating = document.createElement("div");
                rating.className = "passwordStrengthRating";
                const ratingLabel = document.createElement("small");
                ratingLabel.textContent = (0, Language_1.getPhrase)("wcf.user.password.strength");
                rating.appendChild(ratingLabel);
                this.score.className = "passwordStrengthScore";
                this.score.dataset.score = "-1";
                rating.appendChild(this.score);
                this.wrapper.appendChild(rating);
                this.verdictResult.type = "hidden";
                this.verdictResult.name = `${this.input.name}_passwordStrengthVerdict`;
                this.wrapper.parentNode.insertBefore(this.verdictResult, this.wrapper);
                this.input.addEventListener("input", (ev) => this.evaluate(ev));
                this.relatedInputs.forEach((input) => input.addEventListener("input", (ev) => this.evaluate(ev)));
                if (this.input.value.trim() !== "") {
                    this.evaluate();
                }
            });
        }
        evaluate(event) {
            const dictionary = flatMap(STATIC_DICTIONARY.concat(this.staticDictionary, this.relatedInputs.map((input) => input.value.trim())), splitIntoWords).filter((value) => value.length > 0);
            const value = this.input.value.trim();
            // To bound runtime latency for really long passwords, consider sending zxcvbn() only
            // the first 100 characters or so of user input.
            const verdict = this.zxcvbn(value.substr(0, 100), dictionary);
            verdict.feedback = this.feedbacker.from_result(verdict);
            this.score.dataset.score = value.length === 0 ? "-1" : verdict.score.toString();
            if (event !== undefined) {
                // Do not overwrite the value on page load.
                Util_1.default.innerError(this.wrapper, verdict.feedback.warning);
            }
            this.verdictResult.value = JSON.stringify(verdict);
        }
    }
    return PasswordStrength;
});

